/**
 * Host create→play **journey** model (#150, FR-9.11).
 *
 * The host's path from creating a game to playing it is a fixed, ordered sequence of
 * pre-play steps. {@link JourneyStepper} renders it as a progress tracker; this module
 * owns the step list and the pure mapper from host state → current step. It is
 * PRESENTATIONAL ORIENTATION ONLY — it drives nothing, gates nothing, and adds no
 * action (FR-9.5). The tracker is shown on the create screen and the pre-play phases
 * (`LOBBY`, `GENERATING`) and hidden during active play and game-over.
 */

import { gamePhaseOf } from '@jeopardy/shared';
import type { GameState } from '@jeopardy/shared';

export type JourneyStep = 'create' | 'invite' | 'generate' | 'play';

/** The ordered steps, with their display labels. Ordinal position drives done/current/upcoming. */
export const JOURNEY_STEPS: readonly { id: JourneyStep; label: string }[] = [
  { id: 'create', label: 'Create Game' },
  { id: 'invite', label: 'Invite Players' },
  { id: 'generate', label: 'Generate Board' },
  { id: 'play', label: 'Play' },
];

/**
 * The host's current journey step, or `null` when the tracker should NOT render:
 * - no game yet (`hasGame` false) → `create` (the create screen);
 * - a game exists but its state hasn't arrived yet → `null` (don't flash a step);
 * - `LOBBY` → `invite` (players join and suggest categories in the lobby — category
 *   suggestion is not a distinct tracker step, since it only advances once a player
 *   acts, which reads oddly — #150 review feedback);
 * - `GENERATING` → `generate`;
 * - any active-play or game-over phase → `null` (tracker hidden — D-150.7).
 */
export function journeyStep(
  hasGame: boolean,
  state: GameState | null,
): JourneyStep | null {
  if (!hasGame) return 'create';
  if (!state) return null;
  switch (gamePhaseOf(state)) {
    case 'LOBBY':
      return 'invite';
    case 'GENERATING':
      return 'generate';
    default:
      return null; // IN_ROUND / CLUE_OPEN / FINAL_* / GAME_OVER
  }
}
